import { api } from '@agency/shared';
import { Button, Callout, Card, Chip, Field, Input, KeyValue, PageHeader, Select, Skeleton, Switch, fmtTimestamp } from '@agency/ui';
import { Plus, Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErrorCallout, Grid, Page, SensitiveButton, UserLink } from '../../app/widgets.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { useApi } from '../../lib/useApi.js';

type FlagValue = boolean | number | string;

export function FeatureFlagsPage() {
  const session = useSession();
  const mutation = useMutation();
  const q = useApi('admin.ops.featureFlags.get');
  const accounts = useApi('admin.accounts.list', { input: { limit: 200 } }, { enabled: session.has(['account.assign', 'account.register', 'audit.read']) });
  const ff = q.data;
  const canPublish = session.has('template.publish');
  const [flags, setFlags] = useState<Record<string, FlagValue>>({});
  const [rumiDisabled, setRumiDisabled] = useState<string[]>([]);
  const [newKey, setNewKey] = useState('');
  const [newType, setNewType] = useState<'boolean' | 'number' | 'string'>('boolean');
  const [addAccount, setAddAccount] = useState('');
  useEffect(() => {
    if (!ff) return;
    setFlags({ ...ff.flags });
    setRumiDisabled([...ff.rumiDisabledAccountIds]);
  }, [ff]);
  const dirty = ff ? JSON.stringify(flags) !== JSON.stringify(ff.flags) || JSON.stringify(rumiDisabled) !== JSON.stringify(ff.rumiDisabledAccountIds) : false;
  const keyValid = /^[a-z][a-z0-9_]{1,63}$/.test(newKey) && !(newKey in flags);

  return (
    <Page>
      <PageHeader
        eyebrow="System operations"
        eyebrowPath="Feature flags"
        title="Feature flags"
        subtitle={ff ? `Version ${ff.version} · ${Object.keys(ff.flags).length} flags · Rumi disabled for ${ff.rumiDisabledAccountIds.length} accounts · updated ${fmtTimestamp(ff.updatedAt)}` : 'Loading flags.'}
        actions={canPublish ? <SensitiveButton variant="primary" disabled={!dirty} loading={mutation.busy} onClick={async () => { if (!ff) return; await mutation.run('admin.ops.featureFlags.set', { input: { flags, rumiDisabledAccountIds: rumiDisabled, revision: ff.revision } }, { title: (d) => `Feature flags published · v${d.version}`, invalidate: ['admin.ops.featureFlags'] }); }}>Publish version</SensitiveButton> : undefined}
      />
      <ErrorCallout error={q.error} onRetry={() => void q.reload()} className="mt-6" />
      {!ff ? (
        <Skeleton height={280} className="mt-6" />
      ) : (
        <Grid>
          <Card title="Flags" subtitle="Published as a content version · clients adopt on next bundle check" span="col-span-12 lg:col-span-7">
            {!canPublish ? <Callout tone="info" className="mb-4">Changes require template.publish and recent authentication.</Callout> : null}
            <ul className="divide-y divide-hair">
              {Object.entries(flags).sort(([a], [b]) => a.localeCompare(b)).map(([key, value]) => (
                <li key={key} className="flex items-center justify-between gap-4 py-2.5">
                  <span className="font-mono text-xs-plus text-ink">{key}</span>
                  <span className="flex items-center gap-2">
                    {typeof value === 'boolean' ? (
                      <Switch aria-label={key} checked={value} disabled={!canPublish} onCheckedChange={(v) => setFlags((p) => ({ ...p, [key]: v }))} />
                    ) : typeof value === 'number' ? (
                      <Input mono type="number" className="w-32" value={value} disabled={!canPublish} onChange={(e) => setFlags((p) => ({ ...p, [key]: Number(e.target.value) }))} aria-label={key} />
                    ) : (
                      <Input mono className="w-56" value={value} disabled={!canPublish} onChange={(e) => setFlags((p) => ({ ...p, [key]: e.target.value }))} aria-label={key} />
                    )}
                    <Chip tone="mute">{typeof value}</Chip>
                    {canPublish ? <Button size="sm" variant="ghost" trailingArrow={false} icon={Trash} aria-label={`Remove ${key}`} onClick={() => setFlags((p) => { const n = { ...p }; delete n[key]; return n; })} /> : null}
                  </span>
                </li>
              ))}
              {Object.keys(flags).length === 0 ? <li className="py-3 text-xs text-mute">No flags defined.</li> : null}
            </ul>
            {canPublish ? (
              <div className="mt-4 flex items-end gap-2 border-t border-hair pt-4">
                <Field label="New flag" className="flex-1" error={newKey && !keyValid ? 'lowercase, digits and underscores; unique' : undefined}><Input mono value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="rumi_full_review" /></Field>
                <Field label="Type" className="w-32"><Select value={newType} onValueChange={(v) => setNewType(v as typeof newType)} options={[{ value: 'boolean', label: 'boolean' }, { value: 'number', label: 'number' }, { value: 'string', label: 'string' }]} /></Field>
                <Button variant="tertiary" icon={Plus} disabled={!keyValid} onClick={() => { setFlags((p) => ({ ...p, [newKey]: newType === 'boolean' ? false : newType === 'number' ? 0 : '' })); setNewKey(''); }}>Add</Button>
              </div>
            ) : null}
          </Card>
          <Card title="Rumi disabled accounts" subtitle="Per-account Rumi disablement (spec section 11)" span="col-span-12 lg:col-span-5">
            <ul className="divide-y divide-hair">
              {rumiDisabled.length === 0 ? <li className="py-2 text-xs text-mute">Rumi is enabled for every account.</li> : rumiDisabled.map((id) => (
                <li key={id} className="flex items-center justify-between py-2 text-[12.5px]">
                  <span>{accounts.data?.items.find((a) => a.id === id)?.displayName ?? id} <span className="font-mono text-2xs text-mute">{id.slice(0, 8)}</span></span>
                  {canPublish ? <Button size="sm" variant="ghost" trailingArrow={false} icon={Trash} aria-label={`Enable Rumi for ${id}`} onClick={() => setRumiDisabled((p) => p.filter((x) => x !== id))} /> : null}
                </li>
              ))}
            </ul>
            {canPublish ? (
              <div className="mt-4 flex items-end gap-2 border-t border-hair pt-4">
                <Field label="Disable for account" className="flex-1"><Select value={addAccount || undefined} placeholder="Choose an account" onValueChange={setAddAccount} options={(accounts.data?.items ?? []).filter((a) => !rumiDisabled.includes(a.id)).map((a) => ({ value: a.id, label: a.displayName }))} /></Field>
                <Button variant="tertiary" icon={Plus} disabled={!addAccount} onClick={() => { setRumiDisabled((p) => [...p, addAccount]); setAddAccount(''); }}>Add</Button>
              </div>
            ) : null}
            <div className="mt-5 border-t border-hair pt-4">
              <KeyValue items={[{ key: 'Version', value: String(ff.version), mono: true }, { key: 'Updated by', value: <UserLink userId={ff.updatedByUserId} /> }, { key: 'Revision', value: String(ff.revision), mono: true }]} />
            </div>
          </Card>
        </Grid>
      )}
    </Page>
  );
}

export type { api as _api };
